import { useMemo } from 'react';

import {
  FormatType,
  getOtherColor,
  ReplotChartNames,
  StackedBarChart,
  type StackedBarData,
} from '@revelio/replots';

import { useCurrencyPreference } from '../../../../shared/user-preferences/hooks/useCurrencyPreference';
import {
  type BreakdownSingleDataSet,
  type BreakdownTimeseriesDataSet,
  DataType,
} from '../DataProvider/types';
import type { MetricValues } from '../PageConfigProvider/types/metric';
import { useChart } from './ChartProvider';
import { usePlotShareData } from './chart-menu/plot-share/usePlotShare';
import { buildStackedBarTimeseriesData } from './utils/build-stacked-bar-timeseries-data';
import { isOtherSegment } from './utils/group-and-filter-data';

export const StackedBar = ({
  data,
  metric,
  hideLegend = false,
}: {
  data: BreakdownSingleDataSet | BreakdownTimeseriesDataSet;
  metric?: MetricValues;
  hideLegend?: boolean;
}) => {
  const { item } = useChart();
  const colorPalette = item?.plot?.colorPalette;
  const { code } = useCurrencyPreference();

  const isTimeseries = data.type === DataType.BREAKDOWN_TIMESERIES;

  const stackedBarChartData: StackedBarData[] = useMemo(
    () =>
      data.type === DataType.BREAKDOWN_TIMESERIES
        ? buildStackedBarTimeseriesData(data.data)
        : data.data.map((entity) => ({
            group: entity.dim,
            values: (entity.breakdown ?? []).map((b) => ({
              label: b.dim,
              value: b.value ?? 0,
              id: b.id,
            })),
          })),
    [data]
  );

  const segmentOrder = useMemo(() => {
    const seen = new Set<string>();
    const nonOther: string[] = [];
    const other: string[] = [];
    const pushSegment = (segment: { dim: string; id?: string | null }) => {
      if (seen.has(segment.dim)) return;
      seen.add(segment.dim);
      if (isOtherSegment(segment)) other.push(segment.dim);
      else nonOther.push(segment.dim);
    };
    if (data.type === DataType.BREAKDOWN_TIMESERIES) {
      data.data.forEach((segment) => {
        pushSegment({ dim: segment.dim, id: segment.id });
      });
    } else {
      data.data.forEach((entity) => {
        (entity.breakdown ?? []).forEach((b) => {
          pushSegment({ dim: b.dim, id: b.id });
        });
      });
    }
    return [...nonOther, ...other];
  }, [data]);

  const format = useMemo(() => {
    const plotFormat = item?.plot?.format;
    if (plotFormat !== undefined) {
      return plotFormat;
    }
    switch (metric) {
      case 'headcount':
        return FormatType.INTEGER;

      case 'tenure':
        return FormatType.YEAR;

      case 'base_salary':
      case 'total_compensation':
      case 'contractor_pay_rate':
      case 'total_spend_salary':
      case 'total_spend_compensation':
        return FormatType.CURRENCY;

      default:
        return FormatType.INTEGER;
    }
  }, [item?.plot?.format, metric]);

  const chartProps = useMemo(
    () => ({
      data: stackedBarChartData,
      hideLegend,
      colorOverride: { Other: getOtherColor() },
      colorPalette,
      format,
      currencyCode: code,
      segmentOrder,
      orientation: item?.plot?.orientation ?? 'horizontal',
      isTimeseries,
      ...(isTimeseries && { highlightMatchingSegments: false }),
    }),
    [
      stackedBarChartData,
      hideLegend,
      colorPalette,
      format,
      code,
      segmentOrder,
      item?.plot?.orientation,
      isTimeseries,
    ]
  );

  usePlotShareData({
    data: stackedBarChartData,
    chartType: ReplotChartNames.StackedBarChartHorizontal,
    chartProps,
  });

  return <StackedBarChart {...chartProps} />;
};
